import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronRight, Trash2, Download, X, AlertTriangle, Check, ChevronUp, ChevronDown, ChevronsUpDown, FileSpreadsheet } from 'lucide-react';
import { getSessions, getProjects, bulkDeleteSessions, getSessionDownloadUrls } from '../services/api';
import { useCurrentUser } from '../context/UserContext';
import { format } from 'date-fns';
import { enrichWithDistrict, buildMetadataCsv, downloadCsv, type MetadataSession } from '../utils/metadataCsv';

interface Session {
  sessionId: string;
  projectId: string;
  enumeratorId: string;
  enumeratorName: string;
  status: string;
  language: string;
  dialect: string;
  location: {
    state: string;
    district: string;
    village: string;
  };
  duration?: number;
  recordingMode?: 'app' | 'gallery';
  createdAt: string;
  completedAt?: string;
}

type SortField = 'createdAt' | 'duration' | 'language' | 'enumerator' | 'recordingMode';
type SortDir = 'asc' | 'desc';

interface DownloadFile {
  s3Key: string;
  filename: string;
  url: string;
  sizeBytes?: number;
}

interface DownloadSession {
  sessionId: string;
  s3Prefix: string;
  enumeratorName: string;
  language: string;
  files: DownloadFile[];
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    completed: 'badge-green',
    uploading: 'badge-blue',
    uploaded: 'badge-blue',
    failed: 'badge-red',
    draft: 'badge-gray',
    recording: 'badge-yellow',
  };
  return <span className={map[status] ?? 'badge-gray'}>{status}</span>;
}

function formatDuration(seconds?: number) {
  if (!seconds) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function formatSize(bytes?: number) {
  if (!bytes) return '';
  if (bytes > 1073741824) return `${(bytes / 1073741824).toFixed(1)} GB`;
  if (bytes > 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
  return `${(bytes / 1024).toFixed(0)} KB`;
}

// ─── Delete Confirm Modal ──────────────────────────────────────────────────────

function DeleteConfirmModal({
  count,
  onConfirm,
  onCancel,
  isLoading,
}: {
  count: number;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
}) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-red-100 rounded-full">
            <AlertTriangle size={20} className="text-red-600" />
          </div>
          <h2 className="text-lg font-bold text-gray-900">
            Delete {count} session{count !== 1 ? 's' : ''}?
          </h2>
        </div>
        <p className="text-sm text-gray-500 mb-6">
          This will permanently delete the session records and all associated media files (video,
          audio, images) from S3. <strong>This action cannot be undone.</strong>
        </p>
        <div className="flex gap-3 justify-end">
          <button className="btn-secondary" onClick={onCancel} disabled={isLoading}>
            Cancel
          </button>
          <button
            className="px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading && (
              <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            )}
            Delete Permanently
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Download Modal ────────────────────────────────────────────────────────────

function DownloadModal({ sessions, onClose }: { sessions: DownloadSession[]; onClose: () => void }) {
  const [copied, setCopied] = useState(false);

  const scriptLines = sessions.flatMap((s) =>
    s.files.map(
      (f) =>
        `mkdir -p "${f.s3Key.substring(0, f.s3Key.lastIndexOf('/'))}" && wget -q -O "${f.s3Key}" "${f.url}"`,
    ),
  );
  const fullScript = [
    '#!/bin/bash',
    `# DataOne bulk download — ${new Date().toISOString().split('T')[0]}`,
    '# Downloads all files, preserving the S3 path structure (raw/projectId/year/month/sessionId/).',
    '',
    ...scriptLines,
    '',
    'echo "Download complete."',
  ].join('\n');

  const downloadScript = () => {
    const blob = new Blob([fullScript], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dataone-download-${new Date().toISOString().split('T')[0]}.sh`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyScript = () => {
    navigator.clipboard.writeText(fullScript);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const totalFiles = sessions.reduce((sum, s) => sum + s.files.length, 0);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Download Sessions</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {sessions.length} session{sessions.length !== 1 ? 's' : ''} · {totalFiles} files · URLs
              valid 24 hours
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            <X size={18} />
          </button>
        </div>

        {/* Session file list */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {sessions.map((s) => (
            <div key={s.sessionId}>
              <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 font-mono">
                {s.s3Prefix}
              </div>
              <div className="space-y-1.5">
                {s.files.map((f) => (
                  <div
                    key={f.s3Key}
                    className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <code className="text-xs text-gray-600 truncate">{f.filename}</code>
                      {f.sizeBytes && (
                        <span className="text-xs text-gray-400 flex-shrink-0">
                          {formatSize(f.sizeBytes)}
                        </span>
                      )}
                    </div>
                    <a
                      href={f.url}
                      download={f.filename}
                      className="ml-3 flex-shrink-0 p-1.5 text-brand-600 hover:bg-brand-50 rounded-lg"
                      title={`Download ${f.filename}`}
                    >
                      <Download size={14} />
                    </a>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer: wget script */}
        <div className="p-6 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
          <p className="text-xs text-gray-500 mb-3">
            Download all files at once, preserving the exact S3 path structure:
          </p>
          <div className="flex gap-2">
            <button onClick={downloadScript} className="btn-primary text-sm flex items-center gap-2">
              <Download size={14} />
              Download Script (.sh)
            </button>
            <button onClick={copyScript} className="btn-secondary text-sm flex items-center gap-2">
              {copied ? <Check size={14} /> : null}
              {copied ? 'Copied!' : 'Copy Script'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function SessionsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAdmin } = useCurrentUser();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [downloadData, setDownloadData] = useState<DownloadSession[] | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const [metaExporting, setMetaExporting] = useState(false);
  const [metaError, setMetaError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['sessions', statusFilter, projectFilter],
    queryFn: () => {
      const params: Record<string, string> = { limit: '5000' };
      if (statusFilter) params.status = statusFilter;
      if (projectFilter) params.projectId = projectFilter;
      return getSessions(params);
    },
  });

  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir(field === 'createdAt' ? 'desc' : 'asc');
    }
  };

  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: () => getProjects({ status: 'all' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (ids: string[]) => bulkDeleteSessions(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
      setSelected(new Set());
      setShowDeleteModal(false);
    },
  });

  const handleDownload = async (ids: string[]) => {
    setIsDownloading(true);
    setDownloadError(null);
    try {
      const result = await getSessionDownloadUrls(ids);
      setDownloadData(result.sessions);
    } catch (e: unknown) {
      setDownloadError(e instanceof Error ? e.message : 'Failed to generate download URLs');
    } finally {
      setIsDownloading(false);
    }
  };

  // Metadata CSV export: pulls ALL sessions with an uploaded video (ignoring the
  // page filters), maps each GPS point to its district offline, and downloads a
  // CSV sortable by district. Admin only.
  const handleMetadataExport = async () => {
    setMetaExporting(true);
    setMetaError(null);
    try {
      const all = await getSessions({ limit: '5000' });
      const rows: MetadataSession[] = (all?.sessions ?? []).filter(
        (s: MetadataSession) =>
          (s.media?.video?.filename || s.media?.audio?.filename) &&
          (s.status === 'uploaded' || s.status === 'completed'),
      );
      if (rows.length === 0) {
        setMetaError('No uploaded recordings to export');
        return;
      }
      const projectNameById: Record<string, string> = {};
      const fieldLabelById: Record<string, string> = {};
      for (const p of projectsData?.projects ?? []) {
        projectNameById[p.projectId] = p.name;
        for (const f of p.metadataForm?.fields ?? []) fieldLabelById[f.id] = f.label;
      }

      const enriched = await enrichWithDistrict(rows);
      const csv = buildMetadataCsv(enriched, projectNameById, fieldLabelById);
      downloadCsv(csv, `dataone-metadata-${new Date().toISOString().split('T')[0]}.csv`);
    } catch (e: unknown) {
      setMetaError(e instanceof Error ? e.message : 'Failed to export metadata');
    } finally {
      setMetaExporting(false);
    }
  };

  const sessions: Session[] = data?.sessions ?? [];
  const projects = projectsData?.projects ?? [];

  const filtered = useMemo(() => {
    const matched = sessions.filter((s) => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        s.sessionId.toLowerCase().includes(q) ||
        s.enumeratorName.toLowerCase().includes(q) ||
        s.language.toLowerCase().includes(q) ||
        s.location?.state?.toLowerCase().includes(q) ||
        s.location?.district?.toLowerCase().includes(q)
      );
    });
    const sorted = [...matched].sort((a, b) => {
      let cmp = 0;
      if (sortField === 'createdAt') {
        cmp = (a.createdAt ?? '').localeCompare(b.createdAt ?? '');
      } else if (sortField === 'duration') {
        cmp = (a.duration ?? 0) - (b.duration ?? 0);
      } else if (sortField === 'language') {
        cmp = (a.language ?? '').localeCompare(b.language ?? '');
      } else if (sortField === 'enumerator') {
        cmp = (a.enumeratorName ?? '').localeCompare(b.enumeratorName ?? '');
      } else if (sortField === 'recordingMode') {
        // Order: App, Uploaded, then unknown (—). Stable secondary by date desc.
        const rank: Record<string, number> = { app: 0, gallery: 1 };
        const ra = rank[a.recordingMode ?? ''] ?? 2;
        const rb = rank[b.recordingMode ?? ''] ?? 2;
        cmp = ra - rb;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [sessions, search, sortField, sortDir]);

  const SortIcon = ({ field }: { field: SortField }) => {
    if (field !== sortField) return <ChevronsUpDown className="w-3 h-3 text-gray-400 inline ml-1" />;
    return sortDir === 'asc' ? (
      <ChevronUp className="w-3 h-3 text-violet-500 inline ml-1" />
    ) : (
      <ChevronDown className="w-3 h-3 text-violet-500 inline ml-1" />
    );
  };

  const allFilteredIds = filtered.map((s) => s.sessionId);
  const allSelected =
    allFilteredIds.length > 0 && allFilteredIds.every((id) => selected.has(id));

  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(allFilteredIds));
    }
  };

  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedIds = [...selected];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sessions</h1>
          <p className="text-gray-500 text-sm mt-1">Browse and download field recording sessions</p>
        </div>
        {isAdmin && (
          <div className="flex flex-col items-end gap-1">
            <button
              className="btn-secondary text-sm flex items-center gap-2 whitespace-nowrap"
              onClick={handleMetadataExport}
              disabled={metaExporting}
              title="Download a CSV of metadata for all uploaded videos, with GPS mapped to districts"
            >
              {metaExporting ? (
                <div className="h-3.5 w-3.5 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
              ) : (
                <FileSpreadsheet size={14} />
              )}
              {metaExporting ? 'Preparing…' : 'Download Metadata'}
            </button>
            {metaError && <span className="text-xs text-red-600">{metaError}</span>}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-48">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            className="input pl-9 text-sm"
            placeholder="Search session ID, enumerator, language, location..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="input w-40 text-sm"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All statuses</option>
          {['completed', 'uploading', 'uploaded', 'failed', 'draft'].map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <select
          className="input w-48 text-sm"
          value={projectFilter}
          onChange={(e) => setProjectFilter(e.target.value)}
        >
          <option value="">All projects</option>
          {projects.map((p: { projectId: string; name: string }) => (
            <option key={p.projectId} value={p.projectId}>{p.name}</option>
          ))}
        </select>
      </div>

      {/* Bulk action bar — visible when rows are selected (admin only) */}
      {isAdmin && selectedIds.length > 0 && (
        <div className="flex items-center gap-3 px-4 py-3 bg-brand-50 border border-brand-200 rounded-xl">
          <span className="text-sm font-medium text-brand-700">
            {selectedIds.length} session{selectedIds.length !== 1 ? 's' : ''} selected
          </span>
          <div className="flex-1" />
          {downloadError && <span className="text-sm text-red-600">{downloadError}</span>}
          <button
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand-700 bg-white border border-brand-300 rounded-lg hover:bg-brand-50 disabled:opacity-50"
            onClick={() => handleDownload(selectedIds)}
            disabled={isDownloading}
          >
            {isDownloading ? (
              <div className="h-3.5 w-3.5 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
            ) : (
              <Download size={14} />
            )}
            Download
          </button>
          <button
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-lg hover:bg-red-50"
            onClick={() => setShowDeleteModal(true)}
          >
            <Trash2 size={14} />
            Delete
          </button>
          <button
            className="p-1 text-gray-400 hover:text-gray-600"
            onClick={() => setSelected(new Set())}
            title="Clear selection"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Results count */}
      <p className="text-sm text-gray-500">
        {isLoading ? 'Loading…' : `${filtered.length} session${filtered.length !== 1 ? 's' : ''}`}
      </p>

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {isAdmin && (
                  <th className="pl-4 pr-2 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                    />
                  </th>
                )}
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Session ID
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap cursor-pointer select-none hover:text-gray-700"
                  onClick={() => handleSort('enumerator')}
                >
                  Enumerator <SortIcon field="enumerator" />
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap cursor-pointer select-none hover:text-gray-700"
                  onClick={() => handleSort('language')}
                >
                  Language <SortIcon field="language" />
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Location
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap cursor-pointer select-none hover:text-gray-700"
                  onClick={() => handleSort('duration')}
                >
                  Duration <SortIcon field="duration" />
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap cursor-pointer select-none hover:text-gray-700"
                  onClick={() => handleSort('recordingMode')}
                >
                  Source <SortIcon field="recordingMode" />
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Status
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap cursor-pointer select-none hover:text-gray-700"
                  onClick={() => handleSort('createdAt')}
                >
                  Date <SortIcon field="createdAt" />
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td colSpan={isAdmin ? 10 : 9} className="px-4 py-8 text-center text-gray-400 text-sm">
                    Loading…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={isAdmin ? 10 : 9} className="px-4 py-8 text-center text-gray-400 text-sm">
                    No sessions found
                  </td>
                </tr>
              ) : (
                filtered.map((session) => (
                  <tr
                    key={session.sessionId}
                    className={`hover:bg-gray-50 cursor-pointer ${selected.has(session.sessionId) ? 'bg-brand-50' : ''}`}
                  >
                    {isAdmin && (
                      <td
                        className="pl-4 pr-2 py-3 w-10"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleOne(session.sessionId);
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selected.has(session.sessionId)}
                          onChange={() => toggleOne(session.sessionId)}
                          className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                        />
                      </td>
                    )}
                    <td
                      className="px-4 py-3"
                      onClick={() => navigate(`/sessions/${session.sessionId}`)}
                    >
                      <code className="text-xs text-gray-500 font-mono">
                        {session.sessionId.substring(0, 8)}…
                      </code>
                    </td>
                    <td
                      className="px-4 py-3 text-sm text-gray-700"
                      onClick={() => navigate(`/sessions/${session.sessionId}`)}
                    >
                      {session.enumeratorName}
                    </td>
                    <td
                      className="px-4 py-3 text-sm text-gray-700"
                      onClick={() => navigate(`/sessions/${session.sessionId}`)}
                    >
                      <div>{session.language}</div>
                      {session.dialect && (
                        <div className="text-xs text-gray-400">{session.dialect}</div>
                      )}
                    </td>
                    <td
                      className="px-4 py-3 text-sm text-gray-600"
                      onClick={() => navigate(`/sessions/${session.sessionId}`)}
                    >
                      <div>{session.location?.state}</div>
                      <div className="text-xs text-gray-400">{session.location?.district}</div>
                    </td>
                    <td
                      className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap"
                      onClick={() => navigate(`/sessions/${session.sessionId}`)}
                    >
                      {formatDuration(session.duration)}
                    </td>
                    <td
                      className="px-4 py-3 text-sm whitespace-nowrap"
                      onClick={() => navigate(`/sessions/${session.sessionId}`)}
                    >
                      {session.recordingMode === 'app' ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-violet-100 text-violet-700 border border-violet-200">
                          App
                        </span>
                      ) : session.recordingMode === 'gallery' ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-sky-100 text-sky-700 border border-sky-200">
                          Uploaded
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td
                      className="px-4 py-3"
                      onClick={() => navigate(`/sessions/${session.sessionId}`)}
                    >
                      <StatusBadge status={session.status} />
                    </td>
                    <td
                      className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap"
                      onClick={() => navigate(`/sessions/${session.sessionId}`)}
                    >
                      {format(new Date(session.createdAt), 'dd MMM yyyy')}
                    </td>
                    <td
                      className="px-4 py-3"
                      onClick={() => navigate(`/sessions/${session.sessionId}`)}
                    >
                      <ChevronRight size={16} className="text-gray-300" />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showDeleteModal && (
        <DeleteConfirmModal
          count={selectedIds.length}
          onConfirm={() => deleteMutation.mutate(selectedIds)}
          onCancel={() => setShowDeleteModal(false)}
          isLoading={deleteMutation.isPending}
        />
      )}

      {downloadData && (
        <DownloadModal sessions={downloadData} onClose={() => setDownloadData(null)} />
      )}
    </div>
  );
}
